import * as React from 'react';
import { MdAdd, MdEdit, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataGrid from '../../Components/Table/DataGrid';
import PageTitle from '../../Components/Common/PageTitle';
import { useAuth } from '../../Context/AuthContext';
import { getClaimSubmission } from '../../Services/Claim360Service';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';

const getSubmissionRows = (response) => {
  const payload = response?.data ?? response;
  const recordCollections = [
    payload,
    payload?.drafts,
    payload?.claims,
    payload?.items,
    payload?.records,
    payload?.results,
    payload?.data,
    payload?.data?.drafts,
    payload?.data?.claims,
    payload?.data?.items,
    payload?.data?.records,
  ];
  const submissions = recordCollections.find(Array.isArray);

  if (!submissions) {
    throw new Error('The claim submission API response did not contain a records array. Check the response in the browser Network tab.');
  }

  return submissions.map((submission) => {
    const claim = submission.claim ?? submission.claim_data ?? submission.claimData ?? submission;
    const insured = claim.insured || {};
    const billing = claim.billing || {};
    const payer = claim.payer || {};
    const serviceLines = claim.serviceLines || claim.service_lines || [];
    const insuredName = [
      insured.name?.person?.firstName ?? insured.name?.person?.first_name,
      insured.name?.person?.lastName ?? insured.name?.person?.last_name,
    ].filter(Boolean).join(' ');

    return {
      ...submission,
      id: submission.id ?? submission.Id ?? submission.claim_id ?? submission.claimId,
      insured_name: insuredName || submission.insured_name || submission.insuredName || '-',
      member_id: insured.memberId ?? insured.member_id ?? submission.member_id ?? submission.memberId ?? '-',
      billing_organization: billing.billingProvider?.name?.organization
        ?? billing.billing_provider?.name?.organization
        ?? submission.billing_organization
        ?? submission.billingOrganization
        ?? '-',
      payer_name: payer.name?.organization ?? submission.payer_name ?? submission.payerName ?? '-',
      payer_id: payer.id ?? submission.payer_id ?? submission.payerId ?? '-',
      total_charge: billing.totalCharge ?? billing.total_charge ?? submission.total_charge ?? submission.totalCharge ?? '-',
      purpose: claim.purpose ?? submission.purpose ?? '-',
      procedure_codes: serviceLines.map((line) => line.procedureCode?.code ?? line.procedure_code?.code)
        .filter(Boolean).join(', ') || '-',
    };
  });
};

export default function ClaimSubmissionList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const organizationId = React.useMemo(() => (
    decodedToken?.organization_id
    || decodedToken?.OrganizationId
    || decodedToken?.organizationId
    || decodedToken?.org_id
    || localStorage.getItem('organizationId')
  ), [decodedToken]);
  const permissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );
  const canView = permissionHas(permissionCodes, PermissionCodes.CLAIM_SUBMISSION_VIEW, decodedToken);
  const canCreate = permissionHas(permissionCodes, PermissionCodes.CLAIM_SUBMISSION_CREATE, decodedToken);
  const canEdit = permissionHas(permissionCodes, PermissionCodes.CLAIM_SUBMISSION_EDIT, decodedToken);
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const loadSubmissions = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await getClaimSubmission(token, organizationId);
      setRows(getSubmissionRows(response));
    } catch (error) {
      console.error('Failed to load claim submissions:', error);
      toast.error(error.message || 'Unable to load claim submissions.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token, organizationId]);

  React.useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => [
      row.insured_name,
      row.member_id,
      row.billing_organization,
      row.payer_name,
      row.payer_id,
      row.procedure_codes,
    ].some((value) => String(value || '').toLowerCase().includes(term)));
  }, [rows, search]);

  const columns = React.useMemo(() => {
    const submissionColumns = [
      { id: 'insured_name', label: 'Insured', minWidth: 190, format: (value, row) => <div><div className="fw-semibold">{value}</div><div className="small text-secondary">Member ID: {row.member_id}</div></div> },
      { id: 'billing_organization', label: 'Billing Organization', minWidth: 190 },
      { id: 'payer_name', label: 'Payer', minWidth: 160, format: (value, row) => <div><div>{value}</div><div className="small text-secondary">Payer ID: {row.payer_id}</div></div> },
      { id: 'total_charge', label: 'Total Charge', minWidth: 120, align: 'right' },
      { id: 'procedure_codes', label: 'Procedure Code(s)', minWidth: 160 },
      { id: 'purpose', label: 'Purpose', minWidth: 140 },
    ];

    if (canView || canEdit) {
      submissionColumns.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => <div className="d-flex align-items-center justify-content-center gap-2">
          {canView && <button type="button" title="View" className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2" style={{ backgroundColor: 'var(--hc-status-info-bg)' }} onClick={() => navigate(APPRoutes.CLAIM_SUBMISSION_VIEW.replace(':id', row.id), { state: { claimSubmission: row } })}><MdVisibility size={16} /></button>}
          {canEdit && <button type="button" title="Edit" className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2" style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }} onClick={() => navigate(APPRoutes.CLAIM_SUBMISSION_EDIT.replace(':id', row.id))}><MdEdit size={16} /></button>}
        </div>,
      });
    }
    return submissionColumns;
  }, [canView, canEdit, navigate]);

  return <>
    <PageTitle title="Claim Submission - Aegis 360" />
    <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
      <div>
        <h4 className="fw-bold mb-1 hc-text-primary fs-3">Claim Submissions</h4>
        <p className="text-secondary mb-0 small">Review submitted claim details.</p>
      </div>
      {canCreate && <button type="button" onClick={() => navigate(APPRoutes.CLAIM_SUBMISSION_FORM)} className="btn hc-bg-primary mt-md-0 mt-2 btn-sm text-white fw-semibold d-flex align-items-center gap-1 px-3 py-2 rounded-3"><MdAdd size={20} /><span>Create Claim</span></button>}
    </div>

    <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
      <div className="row g-2 mb-3">
        <div className="col-12 col-md-6">
          <div className="position-relative">
            <span className="position-absolute top-50 translate-middle-y text-secondary" style={{ left: '12px' }}><MdSearch size={18} /></span>
            <input type="text" className="form-control form-control-sm hc-input" placeholder="Search insured, member, payer or procedure" value={search} onChange={(event) => setSearch(event.target.value)} style={{ paddingLeft: '36px', height: '40px' }} />
          </div>
        </div>
      </div>
      <DataGrid columns={columns} rows={filteredRows} defaultRowsPerPage={10} stickyHeader getRowId={(row) => row.id} loading={loading} />
    </div>

  </>;
}